// Settings → TopstepX connection (topstepx.js on the server): who's connected, the last sync,
// Sync now and Disconnect. Adding TopstepX accounts happens in Accounts → Add account.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import { ProLock } from './Plans';
import { useI18n } from './i18n';
import { currentLocale } from './langState';

function ago(date, t) {
  const min = Math.round((Date.now() - new Date(date)) / 60000);
  if (min < 1) return t('just now');
  if (min < 60) return t('{n} min ago', { n: min });
  return new Date(date).toLocaleString(currentLocale(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function TopstepSync({ user, onSynced, onSessionExpired }) {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [confirmOff, setConfirmOff] = useState(false);

  useEffect(() => {
    api('/topstepx?lite=1').then(setS).catch((err) => (err.status === 401 ? onSessionExpired() : setS({ available: false })));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function run(kind, fn) {
    setBusy(kind);
    setError('');
    setNotice('');
    try { await fn(); } catch (err) { if (err.status === 401) return onSessionExpired(); setError(err.message); } finally { setBusy(''); }
  }
  const syncNow = () => run('sync', async () => {
    const r = await api('/topstepx/sync', { method: 'POST' });
    setS((x) => ({ ...x, ...r }));
    setNotice(t('{n} new trades synced.', { n: r.result ? r.result.added || 0 : 0 }));
    if (onSynced) onSynced();
  });
  const disconnect = () => run('disconnect', async () => { setS(await api('/topstepx', { method: 'DELETE' })); setConfirmOff(false); });

  if (!s || !s.available || user.isDemo) return null;
  const linked = (s.links || []).filter((l) => l.accountId).length;
  if (s.locked) {
    return (
      <section className="settings-card" id="topstepx">
        <h2>{t('TopstepX connection')}</h2>
        <ProLock compact text={s.connected ? t('Live sync is paused: it’s part of Pro. Your synced trades stay in your journal.') : t('Live sync is part of Pro.')} />
        {s.connected && (
          <div className="settings-actions">
            {confirmOff ? (
              <>
                <span className="field-hint">{t('Stop all syncing and forget the API key? Trades already synced stay.')}</span>
                <button type="button" className="danger small" onClick={disconnect} disabled={Boolean(busy)}>{t('Disconnect')}</button>
                <button type="button" className="link-button" onClick={() => setConfirmOff(false)}>{t('Cancel')}</button>
              </>
            ) : <button type="button" className="link-button" onClick={() => setConfirmOff(true)}>{t('Disconnect')}</button>}
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="settings-card" id="topstepx">
      <h2>{t('TopstepX connection')}</h2>
      {!s.connected ? (
        <p className="settings-text">
          {t('Not connected. To sync TopstepX accounts, go to Accounts, click Add account and choose TopstepX → Live sync.')}{' '}
          <Link to="/app/accounts/new">{t('Add a TopstepX account')} &rarr;</Link>
        </p>
      ) : (
        <>
          <div className="push-device">
            <span className={`push-dot${s.lastError ? '' : ' on'}`} aria-hidden="true" />
            <strong>{t('Connected as {name}', { name: s.userName })}</strong>
            <span className="field-hint">
              {t(linked === 1 ? '{n} account syncing' : '{n} accounts syncing', { n: linked })}
              {s.lastSyncAt ? ` · ${t('Last sync {when}', { when: ago(s.lastSyncAt, t) })}` : ''}
            </span>
          </div>
          {s.lastError && <p className="error">{t(s.lastError)}</p>}
          <p className="settings-text">
            {t('Add or remove synced accounts from the Accounts page: Add account to sync another one, or the gear on an account to stop syncing it.')}
          </p>
          <div className="settings-actions">
            {linked > 0 && <button type="button" className="small" onClick={syncNow} disabled={Boolean(busy)}>{busy === 'sync' ? t('Syncing…') : t('Sync now')}</button>}
            <Link className="secondary-link" to="/app/accounts/new">{t('Add a TopstepX account')}</Link>
            {confirmOff ? (
              <>
                <span className="field-hint">{t('Stop all syncing and forget the API key? Trades already synced stay.')}</span>
                <button type="button" className="danger small" onClick={disconnect} disabled={Boolean(busy)}>{t('Disconnect')}</button>
                <button type="button" className="link-button" onClick={() => setConfirmOff(false)}>{t('Cancel')}</button>
              </>
            ) : <button type="button" className="link-button" onClick={() => setConfirmOff(true)}>{t('Disconnect')}</button>}
          </div>
        </>
      )}
      {notice && <p className="success">{notice}</p>}
      {error && <p className="error">{error}</p>}
    </section>
  );
}
